import { CurrencyAmount } from '@kodiak-finance/sdk-core'
import Column from 'components/Column'
import ConfigurationCard from 'components/KodiakFarms/ConfigurationCard'
import StatusCard from 'components/KodiakFarms/StatusCard'
import { Container, InfoAlert, InfoText } from 'components/KodiakFarms/styled'
import { FarmConfig, RewardToken } from 'components/KodiakFarms/types'
import { hasConfigChanged } from 'components/KodiakFarms/utils'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import Step from 'components/StepLine/common/Step'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { useAllTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { FarmError, FarmErrors, useFarmActions } from 'lib/hooks/kodiakFarms/useFarmAction'
import useFarmConfig from 'lib/hooks/kodiakFarms/useFarmConfig'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Info } from 'react-feather'
import { useParams } from 'react-router-dom'
import { ThemedText } from 'theme'
import { parseUnits } from 'viem'
import { Address } from 'viem'

import StartFarmStep from './StartFarmStep'

const FarmConfiguration: React.FC = () => {
  const { farmId } = useParams<{ farmId: Address }>()
  const { account } = useConnection()
  const { farmConfig, loading: farmConfigLoading, refetch } = useFarmConfig(farmId ?? '0x')
  const { updateLiveConfig, startFarm, updateInitialConfig, validateConfig, checkRewardTokenBalances } = useFarmActions(
    farmId ?? '0x'
  )

  // Ref to track if we're currently checking balances
  const isCheckingBalances = useRef(false)
  // Ref to track last checked config
  const lastCheckedConfig = useRef<string>('')

  // State
  const [currentStep, setCurrentStep] = useState<'edit' | 'review'>('edit')
  const [showStartModal, setShowStartModal] = useState(false)
  const [config, setConfig] = useState<FarmConfig | null>(null)
  const [startingFarm, setStartingFarm] = useState(false)
  const [txHash, setTxHash] = useState<string>('')
  const [errors, setErrors] = useState<FarmErrors>({})
  const [loading, setLoading] = useState(false)
  const [tokenBalances, setTokenBalances] = useState<any[]>([])
  const [balanceCheckLoading, setBalanceCheckLoading] = useState(false)

  const allTokens = useAllTokens()
  const farmStatus = farmConfig?.isLive ? 'active' : 'not_started'

  const checkBalances = useCallback(
    async (checkConfig: FarmConfig, originalConfig: FarmConfig | undefined, mode: 'update' | 'start' = 'update') => {
      if (isCheckingBalances.current || !checkConfig) return []

      try {
        isCheckingBalances.current = true
        setBalanceCheckLoading(true)

        // Check for wallet connection first
        if (!account) {
          const walletError = {
            type: 'VALIDATION_ERROR',
            field: 'wallet',
            message: 'Please connect your wallet to continue.',
          }
          setErrors({ error: walletError as FarmError })
          throw walletError
        }

        const configString = JSON.stringify({
          rewards: checkConfig.rewardTokens.map((t) => ({ address: t.tokenAddress, rate: t.rewardRate })),
          duration: checkConfig.rewardsDuration,
        })

        if (configString === lastCheckedConfig.current) {
          return tokenBalances
        }

        const configCopy = JSON.parse(JSON.stringify(checkConfig))

        try {
          const relevantBalances = await checkRewardTokenBalances(configCopy, originalConfig, mode)

          const completeBalances = checkConfig.rewardTokens.map((token) => {
            const relevantBalance = relevantBalances.find(
              (b) => b.token.toLowerCase() === token.tokenAddress.toLowerCase()
            )

            if (relevantBalance) {
              return relevantBalance
            }

            return {
              token: token.tokenAddress,
              symbol: allTokens[token.tokenAddress]?.symbol || 'Unknown',
              required: parseFloat(token.rewardRateFormatted || '0'),
              available: 0, // Doesn't matter since we don't need approval
              hasEnough: true,
              decimals: allTokens[token.tokenAddress]?.decimals || 18,
              source: 'no_approval_needed',
            }
          })

          setTokenBalances(completeBalances)
          lastCheckedConfig.current = configString
          return completeBalances
        } catch (error) {
          console.error('Balance check error:', error)

          // Create a standardized error object regardless of the original error structure
          let standardError

          // Check if it's already a balance error
          if (error && typeof error === 'object' && error.type === 'BALANCE_ERROR') {
            standardError = {
              ...error,
              message: error.message || `Insufficient ${error.symbol || 'token'} balance`,
            }
          }
          // Check if it's a wallet error
          else if (
            error &&
            typeof error === 'object' &&
            (error.code === 4001 ||
              error.message?.includes('user rejected') ||
              error.message?.includes('User rejected'))
          ) {
            standardError = {
              type: 'VALIDATION_ERROR',
              field: 'wallet',
              message: 'Transaction was rejected by the user. Please try again.',
            }
          }
          // Check if it's a provider/RPC error
          else if (
            error &&
            typeof error === 'object' &&
            (error.message?.includes('network') || error.message?.includes('connection'))
          ) {
            standardError = {
              type: 'VALIDATION_ERROR',
              field: 'network',
              message: 'Network connection error. Please check your internet connection and try again.',
            }
          }
          // Default to a generic balance error
          else {
            standardError = {
              type: 'BALANCE_ERROR',
              field: 'balance',
              message: 'Unable to verify token balances. Please try again.',
              token: '',
              symbol: 'token',
              required: '0',
              available: '0',
            }
          }

          // Set the standardized error
          setErrors({ error: standardError })

          // Always throw to prevent proceeding to review
          throw standardError
        }
      } catch (error) {
        console.error('Error checking token balances:', error)

        // If the error wasn't already processed in the inner catch
        if (!error.type) {
          const genericError = {
            type: 'GENERIC_ERROR',
            field: 'general',
            message: 'An unexpected error occurred. Please try again or reset your changes.',
          }
          setErrors({ error: genericError as FarmError })
          throw genericError
        }

        // Re-throw the already processed error
        throw error
      } finally {
        setBalanceCheckLoading(false)
        isCheckingBalances.current = false
      }
    },
    [checkRewardTokenBalances, allTokens, tokenBalances, account]
  )

  useEffect(() => {
    if (!config || !farmConfig) return

    if (currentStep !== 'review' && !showStartModal) return

    const timer = setTimeout(() => {
      const hasSignificantChanges = hasConfigChanged(config, farmConfig)

      if (hasSignificantChanges) {
        checkBalances(config, farmConfig, farmConfig.isLive ? 'update' : 'start')
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [config, farmConfig, currentStep, showStartModal, checkBalances])

  const getRewardTokenAmount = useCallback(
    (rewardToken?: RewardToken, requiredAmount?: number) => {
      if (!rewardToken || !config || !allTokens[rewardToken.tokenAddress]) return undefined

      const amountToApprove =
        requiredAmount !== undefined ? requiredAmount.toString() : rewardToken.rewardRateFormatted || ''

      if (!amountToApprove || amountToApprove === '') return undefined

      return CurrencyAmount.fromRawAmount(
        allTokens[rewardToken.tokenAddress],
        parseUnits(amountToApprove, allTokens[rewardToken.tokenAddress].decimals).toString()
      )
    },
    [allTokens, config]
  )

  const approveStep0Amount = useMemo(() => {
    if (!config || !tokenBalances.length) return undefined

    const token0 = config.rewardTokens[0]
    if (!token0) return undefined

    const balance = tokenBalances.find((b) => b.token.toLowerCase() === token0.tokenAddress.toLowerCase())

    if (balance && balance.source === 'wallet') {
      const amount = getRewardTokenAmount(token0, balance.required)
      return amount
    }

    return undefined
  }, [config, tokenBalances, getRewardTokenAmount])

  const approveStep1Amount = useMemo(() => {
    if (!config || !tokenBalances.length) return undefined

    const token1 = config.rewardTokens[1]
    if (!token1) return undefined

    const balance = tokenBalances.find((b) => b.token.toLowerCase() === token1.tokenAddress.toLowerCase())

    if (balance && balance.source === 'wallet') {
      const amount = getRewardTokenAmount(token1, balance.required)
      return amount
    }

    return undefined
  }, [config, tokenBalances, getRewardTokenAmount])

  const approveStep2Amount = useMemo(() => {
    if (!config || !tokenBalances.length) return undefined

    const token2 = config.rewardTokens[2]
    if (!token2) return undefined

    const balance = tokenBalances.find((b) => b.token.toLowerCase() === token2.tokenAddress.toLowerCase())

    if (balance && balance.source === 'wallet') {
      const rate = token2.rewardRateFormatted || (balance.required ? balance.required.toString() : '0')

      if (rate === '0' || !rate) {
        return undefined
      }

      try {
        const amount = CurrencyAmount.fromRawAmount(
          allTokens[token2.tokenAddress],
          parseUnits(rate, allTokens[token2.tokenAddress]?.decimals || 18).toString()
        )

        return amount
      } catch (error) {
        console.error('Error creating currency amount:', error)
        return undefined
      }
    }

    return undefined
  }, [config, tokenBalances])

  const approveStep0 = useApproveStep(approveStep0Amount, farmId)
  const approveStep1 = useApproveStep(approveStep1Amount, farmId)
  const approveStep2 = useApproveStep(approveStep2Amount, farmId)

  const onInitialStartFarm = async () => {
    if (!config || !account) return

    if (!account) {
      setErrors({
        error: {
          type: 'VALIDATION_ERROR',
          field: 'startFarm',
          message: 'Please connect your wallet to start the farm.',
        },
      })
      return
    }

    try {
      // Clear previous errors
      setErrors({})

      // Check balances for 'start' mode
      await checkBalances(config, undefined, 'start')

      // If we get here without an error, we can proceed
      setShowStartModal(true)
    } catch (error) {
      console.error('Error starting farm:', error)
      if (error.type === 'BALANCE_ERROR' || error.type === 'VALIDATION_ERROR') {
        setErrors({ error })
      } else {
        setErrors({
          error: {
            type: 'VALIDATION_ERROR',
            field: 'startFarm',
            message: 'An unexpected error occurred. Please try again.',
          },
        })
      }
    }
  }

  const onFarmStart = async () => {
    try {
      setStartingFarm(true)
      const txn = await startFarm(config as FarmConfig)
      setTxHash(txn)
      await refetch()
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      setStartingFarm(false)
    }
  }

  const startFarmStep = useStepWithNext('startFarm', StartFarmStep, {
    config,
    onStart: onFarmStart,
    attemptingTxn: startingFarm,
    pendingText: 'Starting farm...',
    hash: txHash,
    autoConfirm: false,
    waitForConfirmation: false,
  })

  const { approvalStepsArray, approvalStepsObject } = useMemo(() => {
    const array: Step[] = []
    const object: {
      approveStep0?: Step
      approveStep1?: Step
      approveStep2?: Step
    } = {}

    if (config?.rewardTokens[0] && approveStep0Amount) {
      array.push(approveStep0)
      object.approveStep0 = approveStep0
    }

    if (config?.rewardTokens[1] && approveStep1Amount) {
      array.push(approveStep1)
      object.approveStep1 = approveStep1
    }

    if (config?.rewardTokens[2] && approveStep2Amount) {
      array.push(approveStep2)
      object.approveStep2 = approveStep2
    }

    return { approvalStepsArray: array, approvalStepsObject: object }
  }, [config, approveStep0, approveStep1, approveStep2, approveStep0Amount, approveStep1Amount, approveStep2Amount])

  useEffect(() => {
    if (farmConfig) {
      setConfig(farmConfig)
    }
  }, [farmConfig])

  const onStepChange = async (step: 'edit' | 'review') => {
    // If we're already at this step, don't do anything
    if (step === currentStep) return

    // For going back to edit, always allow
    if (step === 'edit') {
      setCurrentStep(step)
      return
    }

    // For going to review, we need to validate several things

    // Clear previous errors first
    setErrors({})
    setLoading(true)

    try {
      // First validate the config
      const validationErrors = await validateConfig(
        config as FarmConfig,
        farmConfig as FarmConfig,
        farmConfig?.isLive ?? false
      )

      if (validationErrors.length > 0) {
        setErrors({ validationErrors })
        return // Don't proceed if there are validation errors
      }

      const isLive = farmConfig?.isLive ?? false

      if (isLive) {
        // Only check balances for live farms being updated
        await checkBalances(config as FarmConfig, farmConfig as FarmConfig, 'update')
      }

      // If we got here, there were no errors - safe to proceed
      setCurrentStep(step)
    } catch (error) {
      console.error('Error during step change:', error)

      // Handle the error but DON'T change steps
      if (error.type === 'BALANCE_ERROR') {
        // Add a helpful message to the error
        error.message = `Insufficient ${error.symbol} balance. Required: ${error.required}, Available: ${error.available}. Please reduce the reward rate or add more tokens.`
        setErrors({ error })
      } else {
        // For other errors
        setErrors({
          error: {
            type: 'VALIDATION_ERROR',
            field: 'general',
            message: 'An unexpected error occurred. Please try again or reset your changes.',
          },
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const hasChanges = useMemo(() => {
    if (!config || !farmConfig) return false
    return hasConfigChanged(config, farmConfig)
  }, [config, farmConfig])

  if (farmConfigLoading || !config) {
    return <div>Loading...</div>
  }

  return (
    <Column gap="1.5rem" width="100%">
      <StepLineModal
        isOpen={showStartModal}
        onDismiss={() => setShowStartModal(false)}
        steps={[...approvalStepsArray, startFarmStep]}
        header={<ThemedText.DeprecatedMediumHeader>Start Farm</ThemedText.DeprecatedMediumHeader>}
        onFinish={() => {
          setShowStartModal(false)
          refetch()
        }}
      />
      <StatusCard
        status={farmStatus}
        onStart={onInitialStartFarm}
        errors={errors}
        isStarting={startingFarm}
        // isLoading={balanceCheckLoading}
      />

      <ConfigurationCard
        step={currentStep}
        config={config}
        allTokens={allTokens}
        onStepChange={onStepChange}
        onConfigChange={setConfig}
        errors={errors}
        loading={loading}
        hasChanges={hasChanges}
        originalConfig={farmConfig as FarmConfig}
        approveSteps={approvalStepsObject}
        onUpdateComplete={refetch}
        updateConfig={farmConfig?.isLive ? updateLiveConfig : updateInitialConfig}
        checkRewardTokenBalances={checkBalances}
        setErrors={setErrors}
        // tokenBalances={tokenBalances}
        isBalanceCheckLoading={balanceCheckLoading}
      />

      {farmStatus === 'not_started' && currentStep === 'edit' && (
        <Container>
          <InfoAlert>
            <Info size={20} />
            <InfoText>
              Remember to start the farm after configuring your parameters. Once started, users will be able to stake
              tokens and earn rewards.
            </InfoText>
          </InfoAlert>
        </Container>
      )}
    </Column>
  )
}

export default FarmConfiguration
